import { nextDelay, pageVisible } from './intervals';

/*
 * One live view of something on the server -- a conversation's new messages,
 * the unread count -- kept current while it is shown.
 *
 *   const live = createLiveChannel({ pull: () => api.get(...), onData, base: 4000 });
 *   live.start();   // asks at once, then again after each answer
 *   live.poke();    // ask now (after sending something, say)
 *   live.stop();
 *
 * TODAY THE TRANSPORT IS POLLING (docs/CHAT.md): `pull` is asked again after
 * every answer, waiting intervals.nextDelay -- nothing while the page is
 * hidden, longer after each failure. A pushed transport (a WebSocket, later)
 * would call `onData` itself and keep this same interface, so the pages that
 * use a live channel do not change. A pull never overlaps the previous one.
 */
export function createLiveChannel({
  pull, onData, onError, base, max, doc = (typeof document !== 'undefined' ? document : null),
  timers = { setTimeout: (...a) => setTimeout(...a), clearTimeout: (...a) => clearTimeout(...a) },
  random,
}) {
  let running = false;
  let timer = null;
  let inFlight = false;
  let failures = 0;

  const clear = () => {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    clear();
    if (!running) return;
    const delay = nextDelay({ visible: pageVisible(doc), failures, base, max, random });
    if (delay === null) return;               // hidden: the visibility listener resumes
    timer = timers.setTimeout(tick, delay);
  };

  function tick() {
    timer = null;
    if (!running || inFlight) return;
    if (!pageVisible(doc)) return;
    inFlight = true;
    let answer;
    try {
      answer = Promise.resolve(pull());
    } catch (e) {
      answer = Promise.reject(e);
    }
    answer
      .then((data) => { failures = 0; if (running) onData?.(data); })
      .catch((e) => { failures += 1; if (running) onError?.(e, failures); })
      .finally(() => { inFlight = false; schedule(); });
  }

  const onVisibility = () => {
    if (running && pageVisible(doc) && timer === null && !inFlight) tick();
  };

  return {
    start() {
      if (running) return;
      running = true;
      doc?.addEventListener?.('visibilitychange', onVisibility);
      tick();
    },
    stop() {
      running = false;
      clear();
      doc?.removeEventListener?.('visibilitychange', onVisibility);
    },
    poke() {
      if (!running || inFlight) return;
      clear();
      tick();
    },
    get failures() { return failures; },
    get running() { return running; },
  };
}
